import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";

import { Box, CircularProgress, Paper } from "@mui/material";
import ModalComp from "app/Components/Modals/ModalTemplate";

import SaveBtn from "app/Components/Buttons/FilledBtns/SaveBtn";
import EditBtn from "app/Components/Buttons/FilledBtns/EditBtn";
import CancelBtn from "app/Components/Buttons/FilledBtns/CancelBtn";

import TextEditor from "app/Components/Fields/TextEditor";

import { getNote, saveNote } from "_DatabaseFunctions/directConnect/users";
import { formatDate } from "utils/helperFunctions";

interface NotesModalProps {
  dets: {
    open: boolean;
    itemName: string;
    itemId: string;
  };
}

interface ModalData {
  title: string;
  type: string;
}

function NotesModal({ dets }: NotesModalProps) {
  const { itemName, itemId } = dets;
  const user = useSelector((state: RootState) => state.user.user);

  const [open, setShow] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [modalData, setModalData] = useState<ModalData>({} as ModalData);
  const [editting, setEditting] = useState(false);
  const [lastEditted, setLastEditted] = useState<string | null>(null);
  const [content, setContent] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setShow(dets.open);
      try {
        const note = await getNote({ userId: user?.uid || "", itemId });
        if (note) {
          setContent(note.content || null);
          setLastEditted(formatDate(note.edittedOn));
        }
      } catch (err) {
        console.log("Error fetching notes", err);
      } finally {
        renderModalInfo();
        setLoading(false);
      }
    })();
  }, [dets]);

  const closeModal = () => setShow(false);

  const renderModalInfo = () => {
    setModalData({
      title: `${itemName} Notes`,
      type: "info",
    });
  };

  const renderLoading = () => (
    <div>{loading ? <CircularProgress /> : <></>}</div>
  );

  const renderNoteContent = () => (
    <Paper sx={{ padding: "50px" }}>
      {content ? (
        <Box
          sx={{
            textAlign: "left",
            display: "inline-block",
          }}
          dangerouslySetInnerHTML={{
            __html: content,
          }}
        />
      ) : (
        <>{`No notes for ${itemName}. Press edit to add new notes`}</>
      )}
    </Paper>
  );

  const renderLastEdittedTime = () => {
    if (lastEditted !== null) {
      return (
        <Box>
          <i>{`Last Editted ${lastEditted}`}</i>
        </Box>
      );
    }
    return null;
  };

  const modalChildren = () => (
    <Box>
      {renderLoading()}
      {editting ? (
        <>
          <TextEditor
            text={content || ""}
            handleChange={(value: string) => setContent(value)}
            placeholder="Add Notes Here"
          />
          {renderLastEdittedTime()}
          <Box sx={{
            margin: "auto",
            width: "150px",
            display: "flex",
            justifyContent: "space-between",
          }}>
            <SaveBtn
              action={() => {
                saveNote({
                  userId: user?.uid || "",
                  itemId,
                  itemName,
                  content: content || "",
                });
                setEditting(false);
                setLastEditted(formatDate(Date.now()));
              }}
              tip="Save Note"
            />
            <CancelBtn
              action={() => {
                setEditting(false);
                closeModal();
              }}
              tip="Cancel"
            />
          </Box>
        </>
      ) : (
        <>
          {renderNoteContent()}
          {renderLastEdittedTime()}
          <Box sx={{
            margin: "auto",
            width: "150px",
            display: "flex",
            justifyContent: "space-between",
          }}>
            <EditBtn action={() => setEditting(true)} tip="Edit" />
            <CancelBtn action={closeModal} tip="Close" />
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <ModalComp
      title={modalData.title || "Notes"}
      modalType="info"
      msg=""
      show={open}
      onClose={closeModal}
    >
      {modalChildren()}
    </ModalComp>
  );
}

export default NotesModal;
